import React from 'react';
import { useState } from 'react';
import { Card, Typography, Button, Chip } from '@material-tailwind/react';
import {
  UserIcon,
  IdentificationIcon,
  MapPinIcon,
  HomeIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  DocumentTextIcon // Pour la liste des diagnostics
} from '@heroicons/react/24/outline';
import { colors } from '../utils/colors';

// Fonction pour obtenir les détails du statut (copiée de PropertySelector pour autonomie)
const getStatusDetails = (status) => {
  switch (status) {
    case 'appointment_confirmed':
      return { label: "RDV confirmé", color: "cyan" };
    case 'appointment_scheduled':
      return { label: "RDV programmé", color: "light-blue" };
    case 'invoice_pending':
      return { label: "Facture en attente", color: "amber" };
    case 'payment_required':
      return { label: "Paiement requis", color: "red" };
    case 'dpe_consent_required':
      return { label: "Accord DPE requis", color: "orange" };
    case 'documents_required':
      return { label: "Documents requis", color: "orange" };
    case 'report_in_preparation':
      return { label: "Rapport en préparation", color: "blue-gray" };
    case 'report_ready':
      return { label: "Rapport prêt", color: "green" };
    default:
      return { label: "Statut inconnu", color: "gray" };
  }
};

export default function PropertyCard({
  property,
  variant = 'informative', // 'informative' or 'actionable'
  onClick,
  ctaLabel,
  onCtaClick,
  showStatus = false,
  showActionsRequired = false,
  className = '',
  smallColumns = false // Renommée de forceTwoColumns
}) {

  // État pour gérer l'affichage des détails sur mobile pour les cartes informatives
  const [showAllDetails, setShowAllDetails] = useState(false);

  if (!property) {
    return null; // Ne rien rendre si la propriété n'est pas fournie
  }

  const hasActions = showActionsRequired && Object.values(property.actionsNeeded || {}).some(needed => needed === true);
  const statusDetails = showStatus ? getStatusDetails(property.propertyStatus) : null;
  const diagnosticsCount = Array.isArray(property.diagnostics) ? property.diagnostics.length : 0;
  const showDiagnosticsSeparately = diagnosticsCount > 6;

  const isCardClickable = variant === 'actionable' && typeof onClick === 'function';
  const showCtaButton = variant === 'actionable' && typeof onCtaClick === 'function' && ctaLabel;

  // Détermine si la logique "Afficher plus" doit s'appliquer
  const applyShowMoreLogic = variant === 'informative' && !hasActions;

  // Nouvelles logiques de classes de visibilité
  let detailsVisibilityClass = 'block';
  let showMoreButtonVisibilityClass = 'hidden';

  if (smallColumns) {
    // Si smallColumns=true, on masque toujours les détails par défaut
    detailsVisibilityClass = showAllDetails ? 'block' : 'hidden';
    // Et le bouton est toujours visible
    showMoreButtonVisibilityClass = 'block';
  } else if (applyShowMoreLogic) {
    // Sinon, si la logique "Afficher plus" s'applique (pas smallColumns):
    // On masque les détails jusqu'à md par défaut
    detailsVisibilityClass = showAllDetails ? 'block' : 'hidden md:block';
    // Et le bouton est visible uniquement jusqu'à md
    showMoreButtonVisibilityClass = 'block md:hidden';
  }

  return (
    <Card
      className={`bg-gray-50 border border-gray-200 shadow-sm transition-all duration-300 w-full ${isCardClickable ? 'hover:bg-gray-100 hover:shadow-md cursor-pointer' : ''} ${className}`}
      onClick={isCardClickable ? onClick : undefined}
    >
      <div className="p-4 sm:p-6">
        <div className="flex justify-between items-start mb-3">
          {/* Nom et ID */}
          <Typography variant="h6" color="blue-gray" className="flex items-center mb-0 mr-2">
            <UserIcon className="h-5 w-5 mr-2 flex-shrink-0" style={{ color: colors.dimoPrimary }} />
            <span className="truncate">{property.name || 'Propriété sans nom'}</span>
          </Typography>
          
          {/* Indicateurs Status / Actions */}
          <div className="flex-shrink-0">
            {hasActions && (
              <div className="flex items-center gap-1 text-orange-600">
                <ExclamationCircleIcon className="h-5 w-5" />
                <Typography variant="small" className="font-medium hidden sm:inline">Action(s) requise(s)</Typography>
                <Typography variant="small" className="font-medium sm:hidden">Action(s)</Typography>
              </div>
            )}
            {statusDetails && !hasActions && (
              <Chip
                variant="ghost"
                size="sm"
                value={statusDetails.label}
                color={statusDetails.color}
                className="text-xs px-1.5 py-0.5"
              />
            )}
          </div>
        </div>

        {/* Container pour les détails pour gérer l'affichage conditionnel global */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${!smallColumns && applyShowMoreLogic && !showAllDetails ? 'max-h-[5rem] md:max-h-full' : 'max-h-full'}`}> 
          {/* La grille est toujours en 1 colonne si smallColumns=true, sinon 1 -> 3 cols à partir de md */}
          <div className={`grid gap-x-6 gap-y-3 text-sm ${showDiagnosticsSeparately ? 'mb-1' : 'mb-4'} ${smallColumns ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'}`}>
            {/* Colonne 1: Dossier, Adresse (Toujours visible) */} 
            <div className="space-y-1">
               <div className="flex items-start space-x-2">
                 <IdentificationIcon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: colors.dimoDark3 }} />
                 <div>
                    <span className="font-medium text-gray-600 mr-1">Dossier:</span>
                    <span className="text-gray-700">#{property.id}</span>
                 </div>
               </div>
               <div className="flex items-start space-x-2">
                 <MapPinIcon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: colors.dimoDark3 }} />
                 <div>
                    <span className="font-medium text-gray-600 mr-1">Adresse:</span>
                    <span className="text-gray-700">{property.address || 'Non définie'}</span>
                 </div>
               </div>
             </div>

            {/* Colonne 2: Type, Superficie (Conditionnel) */} 
             <div className={`space-y-1 ${smallColumns ? 'mt-1 sm:mt-0' : ''} ${detailsVisibilityClass}`}>
               <div className="flex items-start space-x-2">
                 <HomeIcon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: colors.dimoDark3 }} />
                 <div>
                    <span className="font-medium text-gray-600 mr-1">Type:</span>
                    <span className="text-gray-700 truncate">{property.typeProperty || 'Non défini'}</span>
                 </div>
               </div>
               <div className="flex items-start space-x-2">
                 <ArrowTopRightOnSquareIcon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: colors.dimoDark3 }} />
                 <div>
                    <span className="font-medium text-gray-600 mr-1">Superficie:</span>
                    <span className="text-gray-700">{property.square ? `${property.square} m²` : 'Non définie'}</span>
                 </div>
               </div>
             </div>

             {/* Colonne 3: RDV, Diagnostics (Conditionnel) */} 
             <div className={`space-y-1 ${smallColumns ? 'mt-1 sm:mt-0' : ''} ${detailsVisibilityClass}`}>
               {property.appointmentText && (
                <div className="flex items-start space-x-2">
                  <ClockIcon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: colors.dimoDark3 }} />
                  <div>
                     <span className="font-medium text-gray-600 mr-1">RDV:</span>
                     <span className="text-gray-700">{property.appointmentText}</span>
                  </div>
                </div>
              )}
               {!showDiagnosticsSeparately && diagnosticsCount > 0 && (
                 <div className="flex items-start space-x-2">
                   <DocumentTextIcon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: colors.dimoDark3 }} />
                   <div>
                     <span className="font-medium text-gray-600 mr-1">Diagnostics:</span>
                     <span className="text-gray-700 leading-relaxed">
                       {property.diagnostics.join(', ')}
                     </span>
                   </div>
                 </div>
               )}
             </div>
          </div>
          {/* Section Diagnostics Séparée (si > 6) (Conditionnel) */} 
          {showDiagnosticsSeparately && (
             <div className={`mt-3 text-sm mb-4 ${smallColumns ? 'mt-1 sm:mt-0' : ''} ${detailsVisibilityClass}`}> 
               <div className="flex items-start space-x-2">
                 <DocumentTextIcon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: colors.dimoDark3 }} />
                 <div>
                   <span className="font-medium text-gray-600 mr-1">Diagnostics:</span>
                   <span className="text-gray-700 leading-relaxed">
                     {property.diagnostics.join(', ')}
                   </span>
                 </div>
               </div>
             </div>
           )}
        </div>
        {/* Bouton Afficher/Masquer détails */}
        {showMoreButtonVisibilityClass !== 'hidden' && (
          <div className={showMoreButtonVisibilityClass}> 
            <Typography
              as="button"
              variant="small"
              color="gray" /* Couleur changée */
              className="font-medium"
              onClick={(e) => {
                e.stopPropagation(); // Évite le déclenchement d'un éventuel onClick sur la carte parente
                setShowAllDetails(!showAllDetails);
              }}
            >
              {showAllDetails ? 'Masquer les détails' : 'Afficher plus de détails'}
            </Typography>
          </div>
        )}
      </div>

      {/* Section Actionnable */}
      {variant === 'actionable' && (
        <div className={`${showCtaButton ? 'p-4 sm:p-6 pt-0' : ''}`}> {/* Padding seulement si CTA */} 
          {showCtaButton && (
             <Button
               fullWidth
               size="sm"
               className="normal-case mt-2" // Ajout d'un peu d'espace au dessus
               style={{ backgroundColor: colors.dimoPrimary }}
               onClick={onCtaClick}
             >
               {ctaLabel}
             </Button>
           )}
          {isCardClickable && (
             <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
               <ChevronRightIcon className="h-6 w-6 text-gray-400" />
             </div>
           )}
        </div>
      )}
    </Card>
  );
} 